/**
 * MarketAnalysis - Análise Comparativa de Mercado
 * 
 * Baseado no Tripé:
 * ⚖️ JUSTIÇA - Dados de todas as referências, sem tendência
 * 🔒 SEGURANÇA - Fontes oficiais verificáveis
 * ✨ SIMPLICIDADE - Comparativo claro, usuário tira conclusões
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTranslation } from '../../translations';
import HistoricalChart from './HistoricalChart';
import SeasonalityChart from './SeasonalityChart';
import ImportComposition from './ImportComposition';

const MarketAnalysis = () => {
  const { t } = useTranslation();
  const [quotations, setQuotations] = useState({});
  const [loading, setLoading] = useState(true);
  const [drc, setDrc] = useState(53);
  const [taxaParticipacao, setTaxaParticipacao] = useState(72); // Taxa implícita média do mercado
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/market/quotations`);
      if (response.ok) {
        const data = await response.json();
        const quotationsMap = {};
        data.quotations.forEach(q => {
          quotationsMap[q.type] = q;
        });
        setQuotations(quotationsMap);
      }
    } catch (error) {
      console.error('Erro ao buscar cotações:', error);
    } finally {
      setLoading(false);
    }
  };

  // Valores de referência
  const precoImportacao = quotations.preco_importacao?.value || 13.91;
  const geb10 = quotations.geb10_apabor?.value || 12.01;
  const geb10MercadoSP = quotations.geb10_estimado?.value || quotations.geb10_mercado_sp?.value || geb10;
  const coaguloAtual = quotations.coagulo?.value || 3.76;
  const custoProducao = quotations.custo_producao_cot?.value || 6.07;
  const custoProducaoCOE = quotations.custo_producao_coe?.value || 5.00;

  // Cálculos comparativos
  const drcDecimal = drc / 100;
  const brl = (v) => Number(v).toFixed(2).replace('.', ',');
  const pct1 = (v) => Number(v).toFixed(1).replace('.', ',');
  const MESES_PT = { '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr', '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago', '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez' };
  const refDate = (d) => {
    if (!d) return '—';
    const m = /^(\d{4})-(\d{2})$/.exec(d);
    return m ? `${MESES_PT[m[2]] || m[2]}/${m[1].slice(2)}` : d;
  };
  const taxaDecimal = taxaParticipacao / 100;

  // Preços calculados com diferentes referências
  const precoComImportacao = precoImportacao * drcDecimal * taxaDecimal;
  const precoComGeb10 = geb10 * drcDecimal * taxaDecimal;
  const precoComGeb10MercadoSP = geb10MercadoSP * drcDecimal * taxaDecimal;

  // Taxa implícita do preço atual
  const taxaImplicitaImportacao = (coaguloAtual / (precoImportacao * drcDecimal)) * 100;
  const taxaImplicitaGeb10 = (coaguloAtual / (geb10 * drcDecimal)) * 100;
  const taxaImplicitaGeb10MercadoSP = (coaguloAtual / (geb10MercadoSP * drcDecimal)) * 100;

  // Margem sobre custos
  const margemSobreCOT = coaguloAtual - custoProducao;
  const margemSobreCOE = coaguloAtual - custoProducaoCOE;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <span className="animate-spin text-3xl">⏳</span>
          <p className="mt-2 text-gray-600">{t('maLoadingData')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com botão de ajuda */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-green-50">
        <CardContent className="py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">📊</span>
              <p className="text-sm text-gray-700">
                Compare as diferentes referências do mercado de borracha natural.
              </p>
            </div>
            <button 
              onClick={() => setShowHelp(!showHelp)}
              className="text-sm px-3 py-1 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 flex items-center gap-1"
            >
              💡 Entenda
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Tooltip de Ajuda */}
      {showHelp && (
        <Card className="border-2 border-amber-300 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex justify-between items-start mb-3">
              <h4 className="font-bold text-amber-800 flex items-center gap-2">
                💡 Como Funciona esta Página
              </h4>
              <button onClick={() => setShowHelp(false)} className="text-amber-600 hover:text-amber-800 text-lg">✕</button>
            </div>
            
            <div className="space-y-4 text-sm">
              {/* O que são as referências */}
              <div className="bg-white p-3 rounded-lg border">
                <p className="font-bold text-gray-800 mb-2">📈 As 3 Referências de Preço:</p>
                <div className="space-y-3 text-xs text-gray-600">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600">●</span>
                    <div>
                      <strong>Preço Importação (IEA):</strong> Custo TOTAL para importar borracha beneficiada. Inclui: preço internacional + câmbio + frete + seguro + impostos (~10,8%) + custos portuários. Serve como "teto" - é o máximo que a indústria pagaria.
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-600">●</span>
                    <div>
                      <strong>GEB-10 APABOR:</strong> Preço de referência calculado pela APABOR (preço internacional + câmbio + custos). Publicação bimestral. É a referência adotada pelas usinas para negociar com produtores.
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-gray-600">●</span>
                    <div>
                      <strong>GEB-10 Mercado SP:</strong> Preço real praticado nas transações do mercado físico paulista. Reflete oferta e demanda atual.
                    </div>
                  </div>
                </div>
              </div>

              {/* O simulador */}
              <div className="bg-white p-3 rounded-lg border">
                <p className="font-bold text-gray-800 mb-2">🔧 Simulador de Cenários:</p>
                <p className="text-xs text-gray-600">
                  Ajuste o <strong>DRC</strong> e a <strong>Taxa de Participação</strong> para simular diferentes cenários de preço.
                  A tabela mostra como cada referência se comporta com seus parâmetros.
                </p>
              </div>

              {/* Taxa implícita */}
              <div className="bg-white p-3 rounded-lg border">
                <p className="font-bold text-gray-800 mb-2">📐 Taxa de Participação Implícita:</p>
                <p className="text-xs text-gray-600">
                  Mostra qual taxa está sendo praticada no mercado atual, calculada a partir do preço real do coágulo (R$ {brl(coaguloAtual)}).
                </p>
                <div className="bg-gray-50 p-2 rounded mt-2 font-mono text-xs">
                  Taxa = Coágulo ÷ (Referência × DRC)
                </div>
              </div>

              {/* Atualização */}
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="font-bold text-green-800 mb-2">🔄 Atualização dos Dados:</p>
                <p className="text-xs text-green-700">
                  Os dados são os mesmos das outras abas. Atualize na aba "📡 Status".
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Título */}
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800 flex items-center gap-2">
            <span className="text-2xl">📊</span>
            Análise Comparativa de Mercado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">
            Compare os valores de referência do mercado da borracha natural. 
            Dados de fontes oficiais para tomada de decisão informada.
          </p>
        </CardContent>
      </Card>

      {/* Cotações Atuais */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-gray-700">📈 Cotações de Referência</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="text-xs text-blue-600">{t('maImportPrice')}</div>
              <div className="text-xl font-bold text-blue-800">R$ {brl(precoImportacao)}</div>
              <div className="text-xs text-gray-500">{refDate(quotations.preco_importacao?.reference_date)}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <div className="text-xs text-green-600">GEB-10 (APABOR)</div>
              <div className="text-xl font-bold text-green-800">R$ {brl(geb10)}</div>
              <div className="text-xs text-gray-500">{refDate(quotations.geb10_apabor?.reference_date)}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600">🛰️ GEB-10 Estimado</div>
              <div className="text-xl font-bold text-gray-800">R$ {brl(geb10MercadoSP)}</div>
              <div className="text-xs text-gray-500">{quotations.geb10_estimado?.reference_date || 'diário (TSR-20 × PTAX)'}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <div className="text-xs text-amber-600">{t('maCoaguloDrc')}</div>
              <div className="text-xl font-bold text-amber-800">R$ {brl(coaguloAtual)}</div>
              <div className="text-xs text-gray-500">{refDate(quotations.coagulo?.reference_date)}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 border border-red-200 col-span-2 md:col-span-1">
              <div className="text-xs text-red-600">{t('maProdCost')}</div>
              <div className="text-xl font-bold text-red-800">R$ {brl(custoProducao)}</div>
              <div className="text-xs text-gray-500">CONAB</div>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 mt-3 text-left" data-testid="geb10-explain-note">
            ℹ️ <strong>GEB-10 (Oficial)</strong> = preço do fechamento bimestral, usado nos contratos — fica fixo até o próximo fechamento.
            O <strong>indicador diário</strong> publicado no site da APABOR/Borracha Natural oscila com a bolsa (TSR-20 × câmbio) e corresponde ao nosso <strong>GEB-10 Estimado 🛰️</strong>. Os dois estão corretos: um é contrato, o outro é expectativa do dia.
          </p>
        </CardContent>
      </Card>

      {/* Simulador */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-gray-700">🔧 Simulador de Cenários</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="text-center sm:text-left">
              <label className="text-sm text-gray-600 block mb-1">DRC (%)</label>
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <button 
                  onClick={() => setDrc(Math.max(40, drc - 1))}
                  className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                >-</button>
                <input
                  type="number"
                  value={drc}
                  onChange={(e) => setDrc(Math.min(70, Math.max(40, parseInt(e.target.value) || 53)))}
                  className="w-20 text-center border rounded py-1"
                />
                <button 
                  onClick={() => setDrc(Math.min(70, drc + 1))}
                  className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                >+</button>
              </div>
            </div>
            <div className="text-center sm:text-left">
              <label className="text-sm text-gray-600 block mb-1">{t('maParticipationRate')}</label>
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <button 
                  onClick={() => setTaxaParticipacao(Math.max(40, taxaParticipacao - 1))}
                  className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                >-</button>
                <input
                  type="number"
                  value={taxaParticipacao}
                  onChange={(e) => setTaxaParticipacao(Math.min(100, Math.max(40, parseInt(e.target.value) || 78)))}
                  className="w-20 text-center border rounded py-1"
                />
                <button 
                  onClick={() => setTaxaParticipacao(Math.min(100, taxaParticipacao + 1))}
                  className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                >+</button>
              </div>
            </div>
          </div>

          <div className="text-xs text-gray-500 mb-4">
            Referências: DRC padrão 53% | Taxa CNA 52% (mín) | Taxa IEA 80%
          </div>
        </CardContent>
      </Card>

      {/* Tabela Comparativa */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-gray-700">📋 Comparativo de Valores</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-gray-600">{t('maReference')}</th>
                  <th className="text-right py-2 text-gray-600">{t('maBaseValue')}</th>
                  <th className="text-right py-2 text-gray-600">{t('maWithDrc')} {drc}%</th>
                  <th className="text-right py-2 text-gray-600">{t('maWithRate')} {taxaParticipacao}%</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b bg-blue-50">
                  <td className="py-2 font-medium">{t('maImportPrice')}</td>
                  <td className="text-right">R$ {brl(precoImportacao)}</td>
                  <td className="text-right">R$ {brl(precoImportacao * drcDecimal)}</td>
                  <td className="text-right font-bold">R$ {brl(precoComImportacao)}</td>
                </tr>
                <tr className="border-b bg-green-50">
                  <td className="py-2 font-medium">GEB-10 APABOR</td>
                  <td className="text-right">R$ {brl(geb10)}</td>
                  <td className="text-right">R$ {brl(geb10 * drcDecimal)}</td>
                  <td className="text-right font-bold">R$ {brl(precoComGeb10)}</td>
                </tr>
                <tr className="border-b bg-gray-100">
                  <td className="py-2 font-medium">🛰️ GEB-10 Estimado</td>
                  <td className="text-right">R$ {brl(geb10MercadoSP)}</td>
                  <td className="text-right">R$ {brl(geb10MercadoSP * drcDecimal)}</td>
                  <td className="text-right font-bold">R$ {brl(precoComGeb10MercadoSP)}</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="py-2 font-medium">
                    {t('maCurrentCoagulo')}
                    <span className="block text-[11px] font-normal text-gray-500">IEA · {refDate(quotations.coagulo?.reference_date)}</span>
                  </td>
                  <td className="text-right" colSpan="3">
                    <span className="font-bold">R$ {brl(coaguloAtual)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Análise da Taxa Implícita */}
          <div className="mt-4 p-4 bg-gray-100 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">📐 Taxa de Participação Implícita</h4>
            <p className="text-sm text-gray-600 mb-2">
              Com base no preço atual do coágulo (R$ {brl(coaguloAtual)}) e DRC {drc}%:
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <div className="text-xs text-gray-500">{t('maOnImportPrice')}</div>
                <div className="text-lg font-bold text-blue-700">{pct1(taxaImplicitaImportacao)}%</div>
              </div>
              <div className="bg-white p-3 rounded border border-green-300">
                <div className="text-xs text-gray-500">{t('maOnGeb10Apabor')}</div>
                <div className="text-lg font-bold text-green-700">{pct1(taxaImplicitaGeb10)}%</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-xs text-gray-500">sobre GEB-10 Estimado 🛰️</div>
                <div className="text-lg font-bold text-gray-700">{pct1(taxaImplicitaGeb10MercadoSP)}%</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Análise de Custos */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-gray-700">💰 Análise de Custos (CONAB)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="text-sm text-gray-600">{t('maCotTotal')}</div>
              <div className="text-xl font-bold text-gray-800">R$ {brl(custoProducao)}/kg</div>
              <div className="text-xs text-gray-500">{t('maCotDesc')}</div>
            </div>
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="text-sm text-gray-600">{t('maCoeTotal')}</div>
              <div className="text-xl font-bold text-gray-800">R$ {brl(custoProducaoCOE)}/kg</div>
              <div className="text-xs text-gray-500">{t('maCoeDesc')}</div>
            </div>
            <div className="p-4 rounded-lg border bg-amber-50">
              <div className="text-sm text-gray-600">{t('maCurrentPrice')}</div>
              <div className="text-xl font-bold text-amber-800">R$ {brl(coaguloAtual)}/kg</div>
              <div className="text-xs text-gray-500">DRC 53% - Mercado</div>
            </div>
          </div>

          {/* Diferenças */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg border ${margemSobreCOT >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="text-sm text-gray-600">{t('maDiffCot')}</div>
              <div className={`text-xl font-bold ${margemSobreCOT >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {margemSobreCOT >= 0 ? '+' : ''} R$ {brl(margemSobreCOT)}/kg
              </div>
              <div className="text-xs text-gray-500">
                {margemSobreCOT >= 0 ? 'Acima do custo total' : 'Abaixo do custo total'}
              </div>
            </div>
            <div className={`p-4 rounded-lg border ${margemSobreCOE >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="text-sm text-gray-600">{t('maDiffCoe')}</div>
              <div className={`text-xl font-bold ${margemSobreCOE >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {margemSobreCOE >= 0 ? '+' : ''} R$ {brl(margemSobreCOE)}/kg
              </div>
              <div className="text-xs text-gray-500">
                {margemSobreCOE >= 0 ? 'Acima do custo efetivo' : 'Abaixo do custo efetivo'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fontes */}
      <Card className="border-gray-200 bg-gray-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-2">
            <span className="text-lg">📚</span>
            <div>
              <p className="text-gray-700 font-medium text-sm">{t('maDataSources')}</p>
              <p className="text-gray-600 text-xs mt-1">
                Preço Importação: IEA/CNA | GEB-10: APABOR | Coágulo: Notícias Agrícolas | 
                Custos: CONAB | Todos os dados são públicos e verificáveis.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tripé */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-center">
            <div>
              <span className="text-xl">⚖️</span>
              <h4 className="font-bold text-green-800 text-sm mt-1 break-words">TRANSPARÊNCIA</h4>
              <p className="text-xs text-green-700">{t('maAllReferences')}</p>
            </div>
            <div>
              <span className="text-xl">🔒</span>
              <h4 className="font-bold text-green-800 text-sm mt-1">SEGURANÇA</h4>
              <p className="text-xs text-green-700">{t('maOfficialSources')}</p>
            </div>
            <div>
              <span className="text-xl">✨</span>
              <h4 className="font-bold text-green-800 text-sm mt-1">SIMPLICIDADE</h4>
              <p className="text-xs text-green-700">{t('maClearData')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráfico Histórico */}
      <HistoricalChart />

      {/* Gráfico de Sazonalidade */}
      <SeasonalityChart />

      {/* Composição do Preço de Importação */}
      <ImportComposition />
    </div>
  );
};

export default MarketAnalysis;
